import { useState, useRef } from "react";
import {
  Modal,
  Stack,
  Group,
  Text,
  Button,
  Badge,
  Divider,
  Box,
  Paper,
  Timeline,
  ThemeIcon,
  ScrollArea,
  Loader,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconPackage,
  IconClock,
  IconCheck,
  IconTruck,
  IconInbox,
  IconPhone,
  IconMail,
  IconFlask,
  IconCube,
  IconCircleCheck,
  IconAlertCircle,
  IconBellOff,
  IconFileTypePdf,
  IconExternalLink,
  IconAlertTriangle,
  IconUserCheck,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  SendoutStatus,
  SendoutRequestType,
  SendOutWithCaseDetails,
  ExternalInstitutionReason,
  SendOutPriority,
} from "../../services/sendOuts.service";
import {
  useSendOutWithCaseDetails,
  useDismissAlert,
} from "../../hooks/useSendOuts";
import LogFollowUpModal from "./LogFollowUpModal";
import LogReceiptModal from "./LogReceiptModal";
import {
  formatExpectedReturnDate,
  formatSendOutInstantDate,
} from "./sendOutDatePresentation";

dayjs.extend(relativeTime);

interface SendOutDetailModalProps {
  opened: boolean;
  onClose: () => void;
  sendOutId: string | null;
  onUpdate?: () => void;
  readOnly?: boolean;
}

function getStatusColor(status: SendoutStatus): string {
  switch (status) {
    case SendoutStatus.PENDING_APPROVAL:
      return "orange";
    case SendoutStatus.REJECTED:
      return "red";
    case SendoutStatus.PENDING_SHIPPING:
      return "yellow";
    case SendoutStatus.SHIPPED:
      return "blue";
    case SendoutStatus.RECEIVED_PARTIALLY:
      return "cyan";
    case SendoutStatus.RECEIVED:
      return "green";
    default:
      return "gray";
  }
}

function getStatusLabel(status: SendoutStatus): string {
  switch (status) {
    case SendoutStatus.PENDING_APPROVAL:
      return "Pending Approval";
    case SendoutStatus.REJECTED:
      return "Rejected";
    case SendoutStatus.PENDING_SHIPPING:
      return "Pending Shipping";
    case SendoutStatus.SHIPPED:
      return "Shipped";
    case SendoutStatus.RECEIVED_PARTIALLY:
      return "Partially Received";
    case SendoutStatus.RECEIVED:
      return "Received";
    default:
      return status;
  }
}

function formatRequestType(type: SendoutRequestType): string {
  switch (type) {
    case SendoutRequestType.PHYSICIAN_REQUEST:
      return "Ordering Physician Requesting Consultation";
    case SendoutRequestType.EXTERNAL_INSTITUTION:
      return "External Request";
    case SendoutRequestType.GCP_CONSULT:
      return "Pathologist Requires Expert Consultation";
    case SendoutRequestType.MATERIAL_RETURN:
      return "Material Return";
    default:
      return type;
  }
}

function formatUserName(user?: { fullName: string } | null): string {
  if (!user) return "N/A";
  return user.fullName;
}

function formatExternalInstitutionReason(
  reason: ExternalInstitutionReason,
  other?: string | null
): string {
  switch (reason) {
    case ExternalInstitutionReason.CONTINUATION_OF_CARE:
      return "Continuation of Care";
    case ExternalInstitutionReason.LEGAL_REQUEST:
      return "Legal Request";
    case ExternalInstitutionReason.OUTSIDE_REQUEST_ADDITIONAL_TESTING:
      return "Outside Request for Additional Testing";
    case ExternalInstitutionReason.OTHER:
      return other ? `Other: ${other}` : "Other";
    default:
      return reason;
  }
}

function getPriorityColor(priority: SendOutPriority): string {
  switch (priority) {
    case SendOutPriority.STAT:
      return "red";
    case SendOutPriority.URGENT:
      return "orange";
    case SendOutPriority.ROUTINE:
      return "blue";
    default:
      return "gray";
  }
}

function getPriorityLabel(priority: SendOutPriority): string {
  switch (priority) {
    case SendOutPriority.STAT:
      return "STAT";
    case SendOutPriority.URGENT:
      return "Urgent";
    case SendOutPriority.ROUTINE:
      return "Routine";
    default:
      return priority;
  }
}

function getConditionBadgeColor(condition?: string): string {
  if (condition === "damaged") return "red";
  return "green";
}

function isEmptyHtml(html?: string | null): boolean {
  if (!html) return true;
  // Use regex for a lightweight check first, then fallback to DOMParser if needed
  // This project uses <p></p> for empty rich text
  const stripped = html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();
  return stripped.length === 0;
}

export default function SendOutDetailModal({
  opened,
  onClose,
  sendOutId,
  onUpdate,
  readOnly,
}: SendOutDetailModalProps) {
  const { data: sendOut, isLoading } = useSendOutWithCaseDetails(
    opened ? sendOutId || undefined : undefined
  );
  const scrollViewportRef = useRef<HTMLDivElement>(null);
  const [followUpModalOpen, { open: openFollowUp, close: closeFollowUp }] =
    useDisclosure(false);
  const [followUpType, setFollowUpType] = useState<"call" | "email">("call");
  const [receiptModalOpen, { open: openReceipt, close: closeReceipt }] =
    useDisclosure(false);
  const dismissAlertMutation = useDismissAlert();

  const handleFollowUpClick = (type: "call" | "email") => {
    setFollowUpType(type);
    openFollowUp();
  };

  const handleDismissAlert = async () => {
    if (!sendOut) return;
    try {
      await dismissAlertMutation.mutateAsync(sendOut.id);
      notifications.show({
        title: "Alert Dismissed",
        message: "This overdue alert will reappear in 7 days",
        color: "blue",
      });
      onUpdate?.();
      onClose();
    } catch {
      notifications.show({
        title: "Error",
        message: "Failed to dismiss alert",
        color: "red",
      });
    }
  };

  const handleFollowUpSuccess = () => {
    closeFollowUp();
    onUpdate?.();
    // Scroll to bottom after data refreshes to show the new follow-up
    setTimeout(() => {
      scrollViewportRef.current?.scrollTo({
        top: scrollViewportRef.current.scrollHeight,
        behavior: "smooth",
      });
    }, 500);
  };

  const handleReceiptSuccess = () => {
    closeReceipt();
    onUpdate?.();
  };

  const isExternalInstitution =
    sendOut?.requestType === SendoutRequestType.EXTERNAL_INSTITUTION ||
    sendOut?.requestType === SendoutRequestType.MATERIAL_RETURN;

  const isInTransit =
    sendOut?.status === SendoutStatus.SHIPPED ||
    sendOut?.status === SendoutStatus.RECEIVED_PARTIALLY;

  // Calculate overdue info
  const ageDays = sendOut?.shippedAt
    ? dayjs().diff(dayjs(sendOut.shippedAt), "day")
    : 0;
  const isOverdue = ageDays >= 30 && isInTransit;

  // Calculate outstanding materials for partial receipts
  const isPartiallyReceived =
    sendOut?.status === SendoutStatus.RECEIVED_PARTIALLY;
  const slidesReturnedSet = new Set(sendOut?.slidesReturned || []);
  const blocksReturnedSet = new Set(sendOut?.blocksReturned || []);
  const totalSlidesSent = sendOut?.slidesSent?.length || 0;
  const totalBlocksSent = sendOut?.blocksSent?.length || 0;
  const totalSlidesReturned = slidesReturnedSet.size;
  const totalBlocksReturned = blocksReturnedSet.size;
  const totalSent = totalSlidesSent + totalBlocksSent;
  const totalReturned = totalSlidesReturned + totalBlocksReturned;
  const isReceived = sendOut?.status === SendoutStatus.RECEIVED;
  const canonicalCaseNumber =
    sendOut?.case?.externalCaseNumber || sendOut?.case?.caseNumber;
  const externalReportFileName = canonicalCaseNumber
    ? `external-report-${canonicalCaseNumber}.pdf`
    : "external-report.pdf";

  // Parse materials condition for the main materials list
  const materialsConditionMap = (() => {
    if (!sendOut?.materialsCondition) return {};
    try {
      return JSON.parse(sendOut.materialsCondition) as Record<string, string>;
    } catch {
      return {};
    }
  })();

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        title={
          <Group>
            <IconPackage size={20} />
            <Text fw={600}>Send-Out Details</Text>
          </Group>
        }
        size="xl"
      >
        {isLoading || !sendOut ? (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        ) : (
          <>
            <ScrollArea.Autosize
              mah="calc(80vh - 150px)"
              offsetScrollbars
              viewportRef={scrollViewportRef}
            >
              <Stack gap="md" p={4}>
                {/* Header Info - single row */}
                <Group justify="space-between">
                  <Box>
                    <Text size="xs" c="dimmed">
                      Case
                    </Text>
                    <Text fw={600}>{sendOut.case?.caseNumber || "N/A"}</Text>
                  </Box>
                  <Box>
                    <Text size="xs" c="dimmed">
                      Status
                    </Text>
                    <Badge
                      color={getStatusColor(sendOut.status)}
                      variant="filled"
                      size="sm"
                    >
                      {getStatusLabel(sendOut.status)}
                      {isOverdue && " - OVERDUE"}
                    </Badge>
                  </Box>
                  <Box>
                    <Text size="xs" c="dimmed">
                      Type
                    </Text>
                    <Badge color="blue" variant="light" size="sm">
                      {formatRequestType(sendOut.requestType)}
                    </Badge>
                  </Box>
                  {sendOut.externalInstitution?.name && (
                    <Box>
                      <Text size="xs" c="dimmed">
                        Destination
                      </Text>
                      <Text fw={500} size="sm">
                        {sendOut.externalInstitution.name}
                        {sendOut.externalInstitution.city &&
                          sendOut.externalInstitution.state && (
                            <Text span size="xs" c="dimmed">
                              {" "}
                              ({sendOut.externalInstitution.city},{" "}
                              {sendOut.externalInstitution.state})
                            </Text>
                          )}
                      </Text>
                    </Box>
                  )}
                </Group>

                {/* Request Details */}
                <Divider />
                <Box>
                  <Text fw={600} mb="sm">
                    Request Details
                  </Text>
                  <Stack gap="xs">
                    <Group gap="xl" align="flex-start" wrap="wrap">
                      {/* Reason */}
                      {(sendOut.externalInstitutionReason ||
                        sendOut.reason) && (
                        <Box style={{ minWidth: 180 }}>
                          <Text size="xs" c="dimmed">
                            Reason
                          </Text>
                          {sendOut.externalInstitutionReason ? (
                            <Text size="sm" fw={500}>
                              {formatExternalInstitutionReason(
                                sendOut.externalInstitutionReason,
                                sendOut.externalInstitutionReasonOther
                              )}
                            </Text>
                          ) : (
                            <div
                              style={{
                                fontSize: "var(--mantine-font-size-sm)",
                                fontWeight: 500,
                              }}
                              // eslint-disable-next-line react/no-danger
                              dangerouslySetInnerHTML={{
                                __html: sendOut.reason || "",
                              }}
                            />
                          )}
                        </Box>
                      )}

                      {/* Priority */}
                      {sendOut.priority && (
                        <Box>
                          <Text size="xs" c="dimmed">
                            Priority
                          </Text>
                          <Badge
                            color={getPriorityColor(sendOut.priority)}
                            variant="filled"
                            size="sm"
                            mt={2}
                          >
                            {getPriorityLabel(sendOut.priority)}
                          </Badge>
                        </Box>
                      )}

                      {/* Ordering Institution */}
                      {(sendOut.orderingInstitution?.name ||
                        sendOut.case?.facility?.name) && (
                        <Box style={{ minWidth: 160 }}>
                          <Text size="xs" c="dimmed">
                            Ordering Institution
                          </Text>
                          <Text size="sm" fw={500}>
                            {sendOut.orderingInstitution?.name ||
                              sendOut.case?.facility?.name}
                          </Text>
                        </Box>
                      )}

                      {/* Contact Person */}
                      {sendOut.contactPerson && (
                        <Box style={{ minWidth: 140 }}>
                          <Text size="xs" c="dimmed">
                            Contact Person
                          </Text>
                          <Text size="sm" fw={500}>
                            {sendOut.contactPerson}
                          </Text>
                        </Box>
                      )}

                      {/* Phone / Fax */}
                      {sendOut.contactPhoneFax && (
                        <Box style={{ minWidth: 130 }}>
                          <Text size="xs" c="dimmed">
                            Phone / Fax
                          </Text>
                          <Text size="sm" fw={500}>
                            {sendOut.contactPhoneFax}
                          </Text>
                        </Box>
                      )}

                      {/* Authorization Reference */}
                      {sendOut.authorizationReference && (
                        <Box style={{ minWidth: 150 }}>
                          <Text size="xs" c="dimmed">
                            Auth. Reference
                          </Text>
                          <Text size="sm" fw={500}>
                            {sendOut.authorizationReference}
                          </Text>
                        </Box>
                      )}

                      {/* Approved By */}
                      {sendOut.approvedBy && (
                        <Box style={{ minWidth: 140 }}>
                          <Text size="xs" c="dimmed">
                            Approved By
                          </Text>
                          <Text size="sm" fw={500}>
                            {formatUserName(sendOut.approvedBy)}
                          </Text>
                          {sendOut.approvedAt && (
                            <Text size="xs" c="dimmed">
                              {formatSendOutInstantDate(sendOut.approvedAt)}
                            </Text>
                          )}
                        </Box>
                      )}

                      {/* Shipped By */}
                      {sendOut.shippedBy && (
                        <Box style={{ minWidth: 140 }}>
                          <Text size="xs" c="dimmed">
                            Shipped By
                          </Text>
                          <Text size="sm" fw={500}>
                            {formatUserName(sendOut.shippedBy)}
                          </Text>
                          {sendOut.shippedAt && (
                            <Text size="xs" c="dimmed">
                              {formatSendOutInstantDate(sendOut.shippedAt)}
                            </Text>
                          )}
                        </Box>
                      )}
                    </Group>

                    {/* Special Instructions (full width) */}
                    {sendOut.specialInstructions &&
                      !isEmptyHtml(sendOut.specialInstructions) && (
                        <Box>
                          <Text size="xs" c="dimmed" mb={4}>
                            Special Instructions
                          </Text>
                          <Paper withBorder p="sm" bg="gray.0" radius="sm">
                            <div
                              style={{
                                fontSize: "var(--mantine-font-size-sm)",
                              }}
                              // eslint-disable-next-line react/no-danger
                              dangerouslySetInnerHTML={{
                                __html: sendOut.specialInstructions,
                              }}
                            />
                          </Paper>
                        </Box>
                      )}

                    {/* Legacy notes fallback */}
                    {sendOut.notes && !isEmptyHtml(sendOut.notes) && (
                      <Box>
                        <Text size="xs" c="dimmed" mb={4}>
                          Notes
                        </Text>
                        <Paper withBorder p="sm" bg="gray.0" radius="sm">
                          <div
                            style={{
                              fontSize: "var(--mantine-font-size-sm)",
                            }}
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{
                              __html: sendOut.notes || "",
                            }}
                          />
                        </Paper>
                      </Box>
                    )}
                  </Stack>
                </Box>

                {/* Rejection Details */}
                {sendOut.status === SendoutStatus.REJECTED &&
                  (sendOut.rejectionReason || sendOut.rejectedBy) && (
                    <>
                      <Divider />
                      <Box>
                        <Group gap="xs" mb="sm">
                          <IconAlertTriangle
                            size={16}
                            color="var(--mantine-color-red-6)"
                          />
                          <Text fw={600} c="red">
                            Rejection Details
                          </Text>
                        </Group>
                        <Paper withBorder p="sm" bg="red.0" radius="sm">
                          {sendOut.rejectedBy && (
                            <Group
                              gap="xs"
                              mb={sendOut.rejectionReason ? 8 : 0}
                            >
                              <Text size="xs" c="dimmed">
                                Rejected by:
                              </Text>
                              <Text size="sm" fw={500}>
                                {formatUserName(sendOut.rejectedBy)}
                              </Text>
                              {sendOut.rejectedAt && (
                                <Text size="xs" c="dimmed">
                                  on{" "}
                                  {formatSendOutInstantDate(sendOut.rejectedAt)}
                                </Text>
                              )}
                            </Group>
                          )}
                          {sendOut.rejectionReason && (
                            <Box>
                              <Text size="xs" c="dimmed" mb={2}>
                                Reason:
                              </Text>
                              <Text size="sm">{sendOut.rejectionReason}</Text>
                            </Box>
                          )}
                        </Paper>
                      </Box>
                    </>
                  )}

                {/* Materials (left) | Timeline (right) */}
                <Group align="flex-start" gap={0} wrap="nowrap">
                  {/* Left: Materials */}
                  <Box style={{ flex: 1, paddingRight: 16 }}>
                    <Text fw={600} mb="sm">
                      {isExternalInstitution
                        ? "Materials Requested"
                        : "Materials Sent"}
                    </Text>
                    {sendOut.resolvedMaterials &&
                    sendOut.resolvedMaterials.length > 0 ? (
                      <Stack gap="sm">
                        {sendOut.resolvedMaterials.map((item) => (
                          <Paper
                            key={item.specimen.id}
                            withBorder
                            p="xs"
                            radius="sm"
                          >
                            <Group gap="xs" mb={4}>
                              <IconFlask
                                size={14}
                                color="var(--mantine-color-indigo-6)"
                              />
                              <Text fw={600} size="sm">
                                {item.specimen.number}
                              </Text>
                              {item.specimen.source && (
                                <Badge color="indigo" variant="light" size="xs">
                                  {item.specimen.source.name}
                                </Badge>
                              )}
                            </Group>
                            {item.blocks.map((block) => {
                              const blockReturned = blocksReturnedSet.has(
                                block.id
                              );
                              const isBlockSent =
                                sendOut.blocksSent?.includes(block.id) ?? false;
                              return (
                                <Box key={block.id} ml="sm" mb={2}>
                                  <Group gap={4} mb={2}>
                                    <IconCube size={10} color="gray" />
                                    <Text
                                      size="xs"
                                      fw={isBlockSent ? 500 : 400}
                                      c={isBlockSent ? undefined : "dimmed"}
                                      fs={isBlockSent ? undefined : "italic"}
                                    >
                                      Block {block.number}
                                    </Text>
                                    {!isBlockSent && (
                                      <Badge
                                        size="xs"
                                        variant="light"
                                        color="gray"
                                      >
                                        parent only — not sent
                                      </Badge>
                                    )}
                                    {isBlockSent &&
                                      (isPartiallyReceived || isReceived) && (
                                        <Group gap={4}>
                                          <Badge
                                            size="xs"
                                            variant="light"
                                            color={
                                              blockReturned ? "green" : "red"
                                            }
                                            leftSection={
                                              blockReturned ? (
                                                <IconCircleCheck size={10} />
                                              ) : (
                                                <IconAlertCircle size={10} />
                                              )
                                            }
                                          >
                                            {blockReturned
                                              ? "returned"
                                              : "outstanding"}
                                          </Badge>
                                          {blockReturned &&
                                            materialsConditionMap[block.id] && (
                                              <Badge
                                                size="xs"
                                                variant="outline"
                                                color={getConditionBadgeColor(
                                                  materialsConditionMap[
                                                    block.id
                                                  ]
                                                )}
                                              >
                                                {materialsConditionMap[
                                                  block.id
                                                ].toUpperCase()}
                                              </Badge>
                                            )}
                                        </Group>
                                      )}
                                  </Group>
                                  {block.slides.length > 0 && (
                                    <Stack gap={1} ml="md">
                                      {block.slides.map((slide) => {
                                        const slideReturned =
                                          slidesReturnedSet.has(slide.id);
                                        return (
                                          <Group key={slide.id} gap={4}>
                                            <Text size="xs" c="dimmed">
                                              Slide {slide.number}
                                            </Text>
                                            {slide.stain && (
                                              <Badge
                                                size="xs"
                                                variant="outline"
                                                color="dark"
                                              >
                                                {slide.stain.code}
                                              </Badge>
                                            )}
                                            {(isPartiallyReceived ||
                                              isReceived) && (
                                              <Group gap={4}>
                                                <Badge
                                                  size="xs"
                                                  variant="light"
                                                  color={
                                                    slideReturned
                                                      ? "green"
                                                      : "red"
                                                  }
                                                  leftSection={
                                                    slideReturned ? (
                                                      <IconCircleCheck
                                                        size={10}
                                                      />
                                                    ) : (
                                                      <IconAlertCircle
                                                        size={10}
                                                      />
                                                    )
                                                  }
                                                >
                                                  {slideReturned
                                                    ? "returned"
                                                    : "outstanding"}
                                                </Badge>
                                                {slideReturned &&
                                                  materialsConditionMap[
                                                    slide.id
                                                  ] && (
                                                    <Badge
                                                      size="xs"
                                                      variant="outline"
                                                      color={getConditionBadgeColor(
                                                        materialsConditionMap[
                                                          slide.id
                                                        ]
                                                      )}
                                                    >
                                                      {materialsConditionMap[
                                                        slide.id
                                                      ].toUpperCase()}
                                                    </Badge>
                                                  )}
                                              </Group>
                                            )}
                                          </Group>
                                        );
                                      })}
                                    </Stack>
                                  )}
                                </Box>
                              );
                            })}
                          </Paper>
                        ))}
                      </Stack>
                    ) : (
                      <Text size="sm" c="dimmed">
                        {isExternalInstitution
                          ? "No specific materials requested — pathologist to determine at approval."
                          : "No materials recorded"}
                      </Text>
                    )}
                  </Box>

                  {/* Vertical Divider */}
                  <Divider orientation="vertical" />

                  {/* Right: Timeline */}
                  <Box
                    style={{
                      minWidth: 260,
                      paddingLeft: 16,
                    }}
                  >
                    <Text fw={600} mb="sm">
                      Timeline
                    </Text>
                    <Timeline
                      active={getTimelineActive(sendOut)}
                      bulletSize={24}
                      lineWidth={2}
                    >
                      <Timeline.Item
                        bullet={<IconClock size={12} />}
                        title="Requested"
                      >
                        <Text size="sm" c="dimmed">
                          {formatSendOutInstantDate(sendOut.createdAt)} by{" "}
                          {formatUserName(sendOut.requestedBy)}
                        </Text>
                      </Timeline.Item>

                      {sendOut.requiresApproval && (
                        <Timeline.Item
                          bullet={<IconCheck size={12} />}
                          title={
                            sendOut.status === SendoutStatus.REJECTED
                              ? "Rejected"
                              : "Approved"
                          }
                        >
                          <Text size="sm" c="dimmed">
                            {sendOut.approvedAt
                              ? `${formatSendOutInstantDate(
                                  sendOut.approvedAt
                                )} by ${formatUserName(sendOut.approvedBy)}`
                              : sendOut.status === SendoutStatus.REJECTED
                              ? sendOut.rejectedAt
                                ? `${formatSendOutInstantDate(
                                    sendOut.rejectedAt
                                  )} by ${formatUserName(sendOut.rejectedBy)}`
                                : `by ${formatUserName(sendOut.rejectedBy)}`
                              : "Pending"}
                          </Text>
                          {sendOut.status === SendoutStatus.REJECTED &&
                            sendOut.rejectionReason && (
                              <Text size="xs" c="red.7" mt={2}>
                                {sendOut.rejectionReason}
                              </Text>
                            )}
                        </Timeline.Item>
                      )}

                      <Timeline.Item
                        bullet={<IconTruck size={12} />}
                        title="Shipped"
                      >
                        <Text size="sm" c="dimmed">
                          {sendOut.shippedAt
                            ? `${formatSendOutInstantDate(sendOut.shippedAt)}${
                                sendOut.shippedBy
                                  ? ` by ${formatUserName(sendOut.shippedBy)}`
                                  : ""
                              }`
                            : "Pending"}
                        </Text>
                        {sendOut.trackingNumber && (
                          <Text size="xs" c="dimmed" mt={2}>
                            Tracking: {sendOut.trackingNumber}
                          </Text>
                        )}
                      </Timeline.Item>

                      <Timeline.Item
                        bullet={<IconClock size={12} />}
                        title="Expected Return"
                      >
                        <Text size="sm" c="dimmed">
                          {sendOut.expectedReturnDate
                            ? `${formatExpectedReturnDate(
                                sendOut.expectedReturnDate
                              )} (30 days)`
                            : "N/A"}
                        </Text>
                        {isOverdue && (
                          <Badge color="red" variant="filled" size="sm" mt={4}>
                            {ageDays} days - OVERDUE
                          </Badge>
                        )}
                      </Timeline.Item>

                      <Timeline.Item
                        bullet={<IconInbox size={12} />}
                        title="Received"
                      >
                        <Text size="sm" c="dimmed">
                          {sendOut.receivedAt
                            ? `${formatSendOutInstantDate(
                                sendOut.receivedAt
                              )} by ${formatUserName(sendOut.receivedBy)}`
                            : sendOut.status ===
                              SendoutStatus.RECEIVED_PARTIALLY
                            ? "Partially received"
                            : "Pending"}
                        </Text>
                        {isPartiallyReceived && totalSent > 0 && (
                          <Badge
                            color="orange"
                            variant="light"
                            size="sm"
                            mt={4}
                          >
                            {totalReturned} of {totalSent} returned
                          </Badge>
                        )}
                      </Timeline.Item>
                    </Timeline>
                  </Box>
                </Group>

                {/* Follow-Up Log */}
                <Divider />
                <Box>
                  <Text fw={600} mb="sm">
                    Follow-Up Log
                  </Text>
                  {!sendOut.followUps || sendOut.followUps.length === 0 ? (
                    <Text size="sm" c="dimmed">
                      No follow-ups recorded
                    </Text>
                  ) : (
                    <Stack gap="xs">
                      {sendOut.followUps.map((fu) => (
                        <Paper key={fu.id} withBorder p="xs" radius="sm">
                          <Group justify="space-between" mb={4}>
                            <Group gap="xs">
                              <ThemeIcon
                                size="xs"
                                variant="light"
                                color={fu.type === "call" ? "blue" : "grape"}
                              >
                                {fu.type === "call" ? (
                                  <IconPhone size={10} />
                                ) : (
                                  <IconMail size={10} />
                                )}
                              </ThemeIcon>
                              <Text size="xs" fw={500} tt="uppercase">
                                {fu.type}
                              </Text>
                              {fu.contactPerson && (
                                <Text size="xs" c="dimmed">
                                  - {fu.contactPerson}
                                </Text>
                              )}
                            </Group>
                            <Text size="xs" c="dimmed">
                              {formatSendOutInstantDate(fu.createdAt)}
                              {fu.loggedBy && ` by ${fu.loggedBy.fullName}`}
                            </Text>
                          </Group>
                          {fu.type === "email" && fu.subject && (
                            <Text size="xs" c="dimmed" mb={2}>
                              Subject: {fu.subject}
                            </Text>
                          )}
                          <div
                            style={{ fontSize: "var(--mantine-font-size-sm)" }}
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{ __html: fu.notes }}
                          />
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Box>

                {/* Receipt Logs - Show when there are receipt log entries */}
                {sendOut.receiptLogs && sendOut.receiptLogs.length > 0 && (
                  <>
                    <Divider />
                    <Box>
                      <Text fw={600} mb="sm">
                        Receipt Log
                      </Text>
                      <Stack gap="xs">
                        {sendOut.receiptLogs
                          .sort(
                            (a, b) =>
                              new Date(b.receivedAt).getTime() -
                              new Date(a.receivedAt).getTime()
                          )
                          .map((log) => {
                            const totalItems =
                              log.slidesReturned.length +
                              log.blocksReturned.length;
                            return (
                              <Paper key={log.id} withBorder p="xs" radius="sm">
                                <Group justify="space-between" mb={4}>
                                  <Group gap="xs">
                                    <ThemeIcon
                                      size="xs"
                                      variant="light"
                                      color="teal"
                                    >
                                      <IconInbox size={10} />
                                    </ThemeIcon>
                                    <Text size="xs" fw={500}>
                                      {totalItems > 0 ? (
                                        <>
                                          {totalItems} item
                                          {totalItems > 1 ? "s" : ""} returned
                                          {(() => {
                                            const damagedCount = Object.values(
                                              log.materialsCondition || {}
                                            ).filter(
                                              (c) => c === "damaged"
                                            ).length;
                                            return damagedCount > 0
                                              ? ` · ${damagedCount} damaged`
                                              : "";
                                          })()}
                                        </>
                                      ) : (
                                        "Report received"
                                      )}
                                    </Text>
                                  </Group>
                                  <Text size="xs" c="dimmed">
                                    {formatSendOutInstantDate(log.receivedAt)}
                                    {log.receivedBy &&
                                      ` - Received by ${log.receivedBy.fullName}`}
                                  </Text>
                                </Group>

                                {/* Show returned blocks */}
                                {log.blocksReturned.length > 0 && (
                                  <Group gap={4} mb={2}>
                                    <Text size="xs" c="dimmed">
                                      Blocks:
                                    </Text>
                                    <Group gap={6}>
                                      {log.blocksReturned.map((blockId) => {
                                        let blockName = blockId.slice(0, 8);
                                        sendOut.resolvedMaterials?.forEach(
                                          (m) => {
                                            m.blocks.forEach((b) => {
                                              if (b.id === blockId) {
                                                blockName = `${m.specimen.number}${b.number}`;
                                              }
                                            });
                                          }
                                        );
                                        const condition =
                                          log.materialsCondition?.[blockId] ||
                                          "good";
                                        return (
                                          <Group key={blockId} gap={4}>
                                            <Text size="xs">{blockName}</Text>
                                            <Badge
                                              size="xs"
                                              variant="light"
                                              color={getConditionBadgeColor(
                                                condition
                                              )}
                                            >
                                              {condition.toUpperCase()}
                                            </Badge>
                                          </Group>
                                        );
                                      })}
                                    </Group>
                                  </Group>
                                )}

                                {/* Show returned slides with specimen-block-slide naming */}
                                {log.slidesReturned.length > 0 && (
                                  <Group gap={4} mb={2}>
                                    <Text size="xs" c="dimmed">
                                      Slides:
                                    </Text>
                                    <Group gap={6} wrap="wrap">
                                      {log.slidesReturned.map((slideId) => {
                                        let slideName = slideId.slice(0, 8);
                                        let stainCode = "";
                                        sendOut.resolvedMaterials?.forEach(
                                          (m) => {
                                            m.blocks.forEach((b) => {
                                              const slide = b.slides.find(
                                                (s) => s.id === slideId
                                              );
                                              if (slide) {
                                                slideName = `${m.specimen.number}${b.number}-${slide.number}`;
                                                stainCode =
                                                  slide.stain?.code || "";
                                              }
                                            });
                                          }
                                        );
                                        const name = stainCode
                                          ? `${slideName} (${stainCode})`
                                          : slideName;
                                        const condition =
                                          log.materialsCondition?.[slideId] ||
                                          "good";
                                        return (
                                          <Group key={slideId} gap={4}>
                                            <Text size="xs">{name}</Text>
                                            <Badge
                                              size="xs"
                                              variant="light"
                                              color={getConditionBadgeColor(
                                                condition
                                              )}
                                            >
                                              {condition.toUpperCase()}
                                            </Badge>
                                          </Group>
                                        );
                                      })}
                                    </Group>
                                  </Group>
                                )}

                                {/* External report link */}
                                {log.externalReportFileKey && (
                                  <Group gap="xs" mb={2}>
                                    <IconFileTypePdf
                                      size={14}
                                      color="var(--mantine-color-red-6)"
                                    />
                                    <Text size="xs">
                                      {externalReportFileName}
                                    </Text>
                                    <Button
                                      size="compact-xs"
                                      variant="light"
                                      leftSection={
                                        <IconExternalLink size={10} />
                                      }
                                      onClick={async () => {
                                        try {
                                          const { caseImagesService } =
                                            await import(
                                              "../../services/caseImages.service"
                                            );
                                          const { url } =
                                            await caseImagesService.getDownloadUrl(
                                              log.externalReportFileKey!,
                                              externalReportFileName
                                            );
                                          window.open(url, "_blank");
                                        } catch {
                                          notifications.show({
                                            title: "Error",
                                            message:
                                              "Failed to get download URL",
                                            color: "red",
                                          });
                                        }
                                      }}
                                    >
                                      View
                                    </Button>
                                  </Group>
                                )}

                                {/* External diagnosis (rich text) */}
                                {log.externalDiagnosis &&
                                  !isEmptyHtml(log.externalDiagnosis) && (
                                    <Box mt={4}>
                                      <Text size="xs" c="dimmed" mb={2}>
                                        External Diagnosis:
                                      </Text>
                                      <div
                                        style={{
                                          fontSize:
                                            "var(--mantine-font-size-sm)",
                                        }}
                                        // eslint-disable-next-line react/no-danger
                                        dangerouslySetInnerHTML={{
                                          __html: log.externalDiagnosis,
                                        }}
                                      />
                                    </Box>
                                  )}

                                {/* Notes (rich text) */}
                                {log.notes && !isEmptyHtml(log.notes) && (
                                  <Box mt={4}>
                                    <Text size="xs" c="dimmed" mb={2}>
                                      Notes:
                                    </Text>
                                    <div
                                      style={{
                                        fontSize: "var(--mantine-font-size-sm)",
                                      }}
                                      // eslint-disable-next-line react/no-danger
                                      dangerouslySetInnerHTML={{
                                        __html: log.notes,
                                      }}
                                    />
                                  </Box>
                                )}
                              </Paper>
                            );
                          })}
                      </Stack>
                    </Box>
                  </>
                )}

                {/* QA Review — shown when a QA reviewer is assigned (external opinion types) */}
                {sendOut.qaReviewer && (
                  <>
                    <Divider />
                    <Box>
                      <Group gap="xs" mb="sm">
                        <IconUserCheck
                          size={16}
                          color="var(--mantine-color-violet-6)"
                        />
                        <Text fw={600}>QA Review</Text>
                        <Badge
                          color={sendOut.isPendingReview ? "orange" : "green"}
                          variant="light"
                          size="sm"
                        >
                          {sendOut.isPendingReview
                            ? "Pending Review"
                            : "Reviewed"}
                        </Badge>
                      </Group>
                      <Stack gap="xs">
                        <Group gap="xl" wrap="wrap">
                          <Box>
                            <Text size="xs" c="dimmed">
                              Assigned Reviewer
                            </Text>
                            <Text size="sm" fw={500}>
                              {sendOut.qaReviewer.fullName}
                            </Text>
                          </Box>
                          {sendOut.externalReferenceNumber && (
                            <Box>
                              <Text size="xs" c="dimmed">
                                Reference Lab Case #
                              </Text>
                              <Text size="sm" fw={500}>
                                {sendOut.externalReferenceNumber}
                              </Text>
                            </Box>
                          )}
                        </Group>
                        {sendOut.externalDiagnosis &&
                          !isEmptyHtml(sendOut.externalDiagnosis) && (
                            <Box>
                              <Text size="xs" c="dimmed" mb={4}>
                                External Diagnosis
                              </Text>
                              <Paper withBorder p="sm" bg="gray.0" radius="sm">
                                <div
                                  style={{
                                    fontSize: "var(--mantine-font-size-sm)",
                                  }}
                                  // eslint-disable-next-line react/no-danger
                                  dangerouslySetInnerHTML={{
                                    __html: sendOut.externalDiagnosis,
                                  }}
                                />
                              </Paper>
                            </Box>
                          )}
                        {sendOut.externalInterpretation && (
                          <Box>
                            <Text size="xs" c="dimmed" mb={4}>
                              Interpretation
                            </Text>
                            <Paper withBorder p="sm" bg="gray.0" radius="sm">
                              <Text size="sm">
                                {sendOut.externalInterpretation}
                              </Text>
                            </Paper>
                          </Box>
                        )}
                      </Stack>
                    </Box>
                  </>
                )}
              </Stack>
            </ScrollArea.Autosize>

            {/* Sticky Footer - Always visible */}
            {isInTransit && !readOnly && (
              <Group
                justify="center"
                pt="md"
                mt="md"
                style={{
                  borderTop: "1px solid var(--mantine-color-gray-3)",
                }}
              >
                <Button
                  leftSection={<IconPhone size={16} />}
                  variant="light"
                  color="blue"
                  onClick={() => handleFollowUpClick("call")}
                >
                  Log Call
                </Button>
                <Button
                  leftSection={<IconMail size={16} />}
                  variant="light"
                  color="grape"
                  onClick={() => handleFollowUpClick("email")}
                >
                  Log Email
                </Button>
                {isOverdue && (
                  <Button
                    leftSection={<IconBellOff size={16} />}
                    variant="light"
                    color="gray"
                    onClick={handleDismissAlert}
                    loading={dismissAlertMutation.isPending}
                  >
                    Dismiss
                  </Button>
                )}
                <Button
                  leftSection={<IconInbox size={16} />}
                  variant="filled"
                  color="teal"
                  onClick={openReceipt}
                >
                  Log Receipt
                </Button>
              </Group>
            )}
          </>
        )}
      </Modal>

      {sendOut && (
        <>
          <LogFollowUpModal
            opened={followUpModalOpen}
            onClose={closeFollowUp}
            sendOutId={sendOut.id}
            defaultType={followUpType}
            defaultContactPerson={
              sendOut.externalInstitution?.contactName || ""
            }
            onSuccess={handleFollowUpSuccess}
          />
          <LogReceiptModal
            opened={receiptModalOpen}
            onClose={closeReceipt}
            sendOut={sendOut}
            onSuccess={handleReceiptSuccess}
          />
        </>
      )}
    </>
  );
}

/**
 * Calculate the active timeline step based on send-out status
 */
function getTimelineActive(sendOut: SendOutWithCaseDetails): number {
  switch (sendOut.status) {
    case SendoutStatus.PENDING_APPROVAL:
      return 0;
    case SendoutStatus.REJECTED:
      return 1;
    case SendoutStatus.PENDING_SHIPPING:
      return sendOut.requiresApproval ? 1 : 0;
    case SendoutStatus.SHIPPED:
      return sendOut.requiresApproval ? 3 : 2;
    case SendoutStatus.RECEIVED_PARTIALLY:
      return sendOut.requiresApproval ? 4 : 3;
    case SendoutStatus.RECEIVED:
      return sendOut.requiresApproval ? 4 : 3;
    default:
      return 0;
  }
}
